import BaseMap from '../../../components/base-map/base-map';
import {LayerPropsWithType, Layers} from '../../../components/base-map/layers/types';
import {useContext, useEffect, useMemo, useRef, useState, memo} from 'react';
import {BBox, FeatureCollection} from 'geojson';
import mapboxConfig from '../../../insight-container/map/map-libs/mapbox-config';
import {NetworkContext} from '../lib/context';
import {CirclePaint, MapMouseEvent} from 'mapbox-gl';
import {observer} from 'mobx-react';
import bbox from '@turf/bbox';
import {BBox2d} from '@turf/helpers/dist/js/lib/geojson';
import Spinner from '../../../components/spinner';
import {safeConsoleError} from '../../../lib/sanitize';
import {Checkbox, IconButton} from '@carbon/react';
import {CheckboxCheckedFilled, ChevronDown, ChevronUp, Group, Hospital, Icon, LocationFilled, Maximize} from '@carbon/icons-react';
import {convertDataToGeoJSONPoints} from '../../../lib/data';
import {PercentFormatter} from '../../../lib/formatter';
import './network-map.scss';
import {SaveRosterButton} from './additional-providers-table/save-roster-button';
import {bboxPolygon, booleanContains} from '@turf/turf';
import {MapRef} from 'react-map-gl';
import ErrorDisplay from './error-display';
import {SPECIALTY_COLOR_PALETTE} from '../lib/specialty-colors';

const INVALID_COLOR = 'rgba(205, 77, 81, 0.6)';
const VALID_COLOR = 'rgba(0, 120, 212, 0.6)';
const NULL_COLOR = 'rgba(79, 93, 117, 0.6)';
const GREYED_OUT_COLOR = 'rgba(120, 120, 120, 0.4)';
const MULTI_SPECIALTY_THRESHOLD = 2;
const PROVIDER_COLOR = 'rgba(255, 222, 102, 1)';
const ADDITIONAL_PROVIDER_COLOR = 'rgba(150, 215, 237, 1)';
const SELECTED_PROVIDER_COLOR = 'rgba(214, 183, 255, 1)';
const PROPERTY_TO_MAP = 'patient_met_flag';
const CONTINENTAL_US_BOUNDS: BBox2d = [-124.7844079, 24.7433195, -66.9513812, 49.3457868];
const ADDITIONAL_PROVIDER_TAB_INDEX = 2;
const CLUSTER_PROPERTIES = ['full_name', 'npi'];

function createDefaultLayerStyle(validColor: string, invalidColor: string, nullColor: string): CirclePaint {
    return {
        'circle-radius': .25,
        'circle-color': ['match', ['get', PROPERTY_TO_MAP], '1', validColor, '0', invalidColor, nullColor ]
    };
}


function createBaseLayers(patientData: FeatureCollection, providerData: FeatureCollection, cluster = true, validColor?: string, invalidColor?: string, nullColor?: string, providerColor?: string): {name: string, icon: typeof Icon, layer: LayerPropsWithType}[] {
    return [{name: 'Patients', icon: Group, layer: {
        id: `layer-1`,
        data: patientData,
        layerType: Layers.circle,
        paint: createDefaultLayerStyle(validColor ?? VALID_COLOR, invalidColor ?? INVALID_COLOR, nullColor ?? NULL_COLOR)
    }},
    {name: 'Providers', icon: Hospital, layer: {
        id: `layer-2`,
        data: providerData,
        layerType: cluster ? Layers.clusteredPoints : Layers.point,
        paint: {'icon-color': providerColor ?? PROVIDER_COLOR},
        clusterProperties: CLUSTER_PROPERTIES
    }}];
}

function createAdditionalProviderLayers(additionalProviders: FeatureCollection, selectedAdditionalProviders: FeatureCollection, cluster = true, additionalColor?: string, selectedColor?: string): {name: string, icon: typeof Icon, layer: LayerPropsWithType}[] {
    return [{name: 'Additional Providers', icon: Hospital, layer: {id: `layer-3`,
        data: additionalProviders,
        layerType: cluster ? Layers.clusteredPoints : Layers.point,
        paint: {'icon-color': additionalColor ?? ADDITIONAL_PROVIDER_COLOR},
        clusterProperties: CLUSTER_PROPERTIES
    }},
    {name: 'Selected Additional Providers', icon: Hospital, layer: {id: `layer-4`,
        data: selectedAdditionalProviders,
        layerType: Layers.point,
        paint: {'icon-color': selectedColor ?? SELECTED_PROVIDER_COLOR},
        layout: {'icon-image': 'check-pin'}
    }}];
}

interface NetworkMapProps {
    setSaveRosterModalOpen: (open: boolean) => void;
    mapDataLoading?: boolean;
    mapDataError?: string | null;
}

const NetworkMap = memo(observer(({setSaveRosterModalOpen, mapDataLoading, mapDataError}: NetworkMapProps) => {
    const loading = useMemo(() => mapDataLoading ?? false, [mapDataLoading]);
    const [_error, setError] = useState<string>('');
    const error = useMemo(() => mapDataError || _error, [mapDataError, _error]);
    const [popupOptions, setPopupOptions] = useState<any>();
    const [disabledLayers, setDisabledLayers] = useState<string[]>([]);
    const networkStore = useContext(NetworkContext);
    const divRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<MapRef | null>(null);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [currentBounds, setCurrentBounds] = useState<BBox2d>(CONTINENTAL_US_BOUNDS);
    const [cluster, setCluster] = useState<boolean>(false);
    const [specialtiesExpanded, setSpecialtiesExpanded] = useState<boolean>(false);
    const [validColor, setValidColor] = useState<string>(VALID_COLOR);
    const [invalidColor, setInvalidColor] = useState<string>(INVALID_COLOR);
    const [nullColor, setNullColor] = useState<string>(NULL_COLOR);
    const [providerColor, setProviderColor] = useState<string>(PROVIDER_COLOR);
    const [additionalProviderColor, setAdditionalProviderColor] = useState<string>(ADDITIONAL_PROVIDER_COLOR);
    const [selectedProviderColor, setSelectedProviderColor] = useState<string>(SELECTED_PROVIDER_COLOR);
    const revisionId = networkStore.revisionId ?? 0;

    // Clear error when loading starts
    useEffect(() => {
        if (loading) {
            setError('');
        }
    }, [loading]);

    // Track fullscreen state changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Reset bounds when specialty filter changes to allow map to zoom to new data
    useEffect(() => {
        setCurrentBounds(CONTINENTAL_US_BOUNDS);
    }, [networkStore.specialtyFilter]);

    const requestFullScreen = () => {
        const element = divRef.current;
        if (!element) return;

        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else if (element.requestFullscreen) {
            element.requestFullscreen();
        }
    };

    function onHoverCB(features: Array<any>) {
        if (!features || !features.length) {
            setPopupOptions(null);
            return;
        }
        const fProps = features[0].properties;
        if (fProps.cluster) {
            const propertiesToShow: Record<string, string[]> = CLUSTER_PROPERTIES.reduce((prev, curr) => {
                return {...prev, [curr]: fProps[curr]?.split('|')};
            }, {});
            setPopupOptions({name: <div className='text-background-gray-900'>
                Providers: {fProps.point_count}
                {fProps.point_count <= 10 && Array.from({length: fProps.point_count}, (_, i) => i).map((index) => 
                    <ul key={index} className='border-b py-1'>{CLUSTER_PROPERTIES.map((p, i) => <li key={p} className={i === 0 ? 'font-semibold' : ''}>{propertiesToShow[p][index]}</li>)}</ul>
                )}
            </div>, latitude: features[0].geometry.coordinates[1], longitude: features[0].geometry.coordinates[0]});
            return;
        }

        const {full_name, npi, practice_location_address_postal_code, marginal_impact_time, marginal_impact_distance} = fProps;

        const popupElement = <ul className='text-background-gray-900'>
            <li><strong>{full_name}</strong></li>
            <li>NPI: {npi}</li>
            <li>Zipcode: {practice_location_address_postal_code}</li>
            {marginal_impact_time && <li>Impact Time: {new PercentFormatter(0.1, null).formatValue(marginal_impact_time)}</li>}
            {marginal_impact_distance && <li>Impact Distance: {new PercentFormatter(0.1, null).formatValue(marginal_impact_distance)}</li>}
        </ul>;
        const coords = features[0].geometry?.coordinates;
        const latitude = fProps.latitude ?? coords?.[1];
        const longitude = fProps.longitude ?? coords?.[0];
        setPopupOptions({name: popupElement, latitude, longitude});
    }

    function onClickCB(event: MapMouseEvent) {
        const features = event.features || [];

        if (features && features.length === 0) {
            return;
        }
        const feature = features[0];
        const isClustured = feature.properties!.cluster;
        const key = feature.properties!.id || feature.properties!['provider_address_fact_key'];
        if(isClustured) {
            mapRef.current?.getMap().zoomIn();
        }
        if (feature.source === 'layer-2') {
            networkStore.existingProviderQueuedForDeletion = key;
        } else if (feature.source === 'layer-3' && !isClustured) {
            networkStore.existingProviderQueuedForDeletion = null;
            if (networkStore.selectedAdditionalProviderKeys.includes(key)) {
                networkStore.deselectAdditionalProviders([key]);
            } else if (networkStore.deselectedExistingProviderKeys.includes(key)) {
                networkStore.selectExistingProviders([key]);
            } else {
                networkStore.selectAdditionalProviders([key]);
            }
        }
    }

    function setLayerEnabled(layerId: string, checked: boolean) {
        setDisabledLayers(checked ? disabledLayers.filter(l => l !== layerId) : disabledLayers.concat(layerId));
    }

    const bounds: BBox = useMemo(() => {
        // Helper function to validate bounds are within valid coordinate ranges
        const isValidBounds = (bounds: BBox2d): boolean => {
            const [minLng, minLat, maxLng, maxLat] = bounds;
            return !isNaN(minLng) && !isNaN(minLat) && !isNaN(maxLng) && !isNaN(maxLat) &&
                   isFinite(minLng) && isFinite(minLat) && isFinite(maxLng) && isFinite(maxLat) &&
                   minLat >= -90 && minLat <= 90 &&
                   maxLat >= -90 && maxLat <= 90 &&
                   minLng >= -180 && minLng <= 180 &&
                   maxLng >= -180 && maxLng <= 180;
        };

        let newBounds: BBox2d = CONTINENTAL_US_BOUNDS as BBox2d;

        // Try to calculate bounds from data, falling back to continental US if invalid
        if (networkStore.patientGeoJson && networkStore.patientGeoJson.features.length > 0) {
            const calculatedBounds = bbox(networkStore.patientGeoJson) as BBox2d;
            if (isValidBounds(calculatedBounds)) {
                newBounds = calculatedBounds;
            }
        } else if (networkStore.providerGeoJson && networkStore.providerGeoJson.features.length > 0) {
            const calculatedBounds = bbox(networkStore.providerGeoJson) as BBox2d;
            if (isValidBounds(calculatedBounds)) {
                newBounds = calculatedBounds;
            }
        } else if (networkStore.multiSpecialtyProviderGeoJson) {
            const allFeatures = Object.values(networkStore.multiSpecialtyProviderGeoJson).flatMap(fc => fc.features);
            if (allFeatures.length > 0) {
                const calculatedBounds = bbox({type: 'FeatureCollection', features: allFeatures}) as BBox2d;
                if (isValidBounds(calculatedBounds)) {
                    newBounds = calculatedBounds;
                }
            }
        }

        const currentBoundsIsSmaller = booleanContains(bboxPolygon(newBounds), bboxPolygon(currentBounds));
        if (!currentBoundsIsSmaller) {
            setCurrentBounds(newBounds);
            return newBounds;
        }

        return currentBounds;
    }, [networkStore.patientGeoJson, networkStore.providerGeoJson, networkStore.multiSpecialtyProviderGeoJson]);


    const allLayers = useMemo(() => {
        let newLayers: {name: string, icon: typeof Icon, layer: LayerPropsWithType}[] = [];

        try {
            const specialtyCount = networkStore.selectedSpecialtyCount;
            const isMultiSpecialty = specialtyCount >= MULTI_SPECIALTY_THRESHOLD;

            if (specialtyCount >= 1) {
                // 1+ specialties: patients (compliance colors for single, greyed for multi)
                // + all providers in yellow as base + per-specialty colored layers on top
                if (networkStore.patientGeoJson) {
                    const pValid = isMultiSpecialty ? GREYED_OUT_COLOR : validColor;
                    const pInvalid = isMultiSpecialty ? GREYED_OUT_COLOR : invalidColor;
                    const pNull = isMultiSpecialty ? GREYED_OUT_COLOR : nullColor;
                    newLayers.push({
                        name: 'Patients',
                        icon: Group,
                        layer: {
                            id: 'layer-1',
                            data: networkStore.patientGeoJson,
                            layerType: Layers.circle,
                            paint: createDefaultLayerStyle(pValid, pInvalid, pNull),
                        },
                    });
                }

                if (networkStore.providerGeoJson) {
                    newLayers.push({
                        name: 'Current Providers',
                        icon: Hospital,
                        layer: {
                            id: 'layer-2',
                            data: networkStore.providerGeoJson,
                            layerType: cluster ? Layers.clusteredPoints : Layers.point,
                            paint: {'icon-color': providerColor ?? PROVIDER_COLOR},
                            clusterProperties: CLUSTER_PROPERTIES,
                        },
                    });
                }

                const batchData = networkStore.multiSpecialtyProviderGeoJson;
                if (batchData) {
                    networkStore.selectedSpecialtyNames.forEach((specialtyName, i) => {
                        const color = SPECIALTY_COLOR_PALETTE[i % SPECIALTY_COLOR_PALETTE.length];
                        const specialtyCode = networkStore.specialtyFilterCode.split('|')[i] ?? specialtyName;
                        const geoJson = batchData[specialtyCode] ?? batchData[specialtyName];
                        if (!geoJson) return;
                        newLayers.push({
                            name: specialtyName,
                            icon: Hospital,
                            layer: {
                                id: `provider-specialty-${i}`,
                                data: geoJson,
                                layerType: cluster ? Layers.clusteredPoints : Layers.point,
                                paint: {'icon-color': color},
                                clusterProperties: CLUSTER_PROPERTIES,
                            },
                        });
                    });
                }
            } else {
                if (!(networkStore.patientGeoJson && networkStore.providerGeoJson))
                    return newLayers;

                const baseLayers = createBaseLayers(networkStore.patientGeoJson, networkStore.providerGeoJson, cluster, validColor, invalidColor, nullColor, providerColor);
                newLayers = newLayers.concat(baseLayers);
            }

            if (networkStore.localProviders.length && networkStore.activeTab === ADDITIONAL_PROVIDER_TAB_INDEX)
                newLayers = newLayers.concat(
                    createAdditionalProviderLayers(
                        convertDataToGeoJSONPoints(networkStore.nonSelectedAdditionalProviders, 'latitude', 'longitude'),
                        convertDataToGeoJSONPoints(networkStore.selectedAdditionalProviders, 'latitude', 'longitude'),
                        cluster,
                        additionalProviderColor,
                        selectedProviderColor));

            return newLayers;
        } catch (error) {
            safeConsoleError('Error creating map layers:', error);
            setError('Unable to display map data. Please try refreshing or selecting a different specialty.');
            return [];
        }
    }, [networkStore.patientGeoJson, networkStore.providerGeoJson, networkStore.multiSpecialtyProviderGeoJson, networkStore.localProviders, networkStore.selectedAdditionalProviders, networkStore.activeTab, networkStore.selectedSpecialtyCount, networkStore.selectedSpecialtyNames, networkStore.specialtyFilterCode, cluster, validColor, invalidColor, nullColor, providerColor, additionalProviderColor, selectedProviderColor]);

    const layers = useMemo(() => 
        allLayers.filter(l => l.layer.data?.features.length).map(l => {
            const isDisabled = disabledLayers.includes(l.layer.id.toString());
            let layerType = l.layer.layerType;
            if (layerType !== Layers.circle)
                layerType = isDisabled || !cluster ? Layers.point : Layers.clusteredPoints;

            return ({...l.layer, layout: {...l.layer.layout, 'visibility': isDisabled ? 'none' : 'visible' as 'none' | 'visible'}, layerType: layerType});
        })
    , [allLayers, disabledLayers, cluster]);

    const interactiveMapLayerIds = useMemo(() => layers.filter(l => l.layerType !== Layers.circle).map(l => l.id as string), [layers]);
    const handleMapRefReady = (ref: MapRef | null) => {
        mapRef.current = ref;
    };

    return <div className="cds--g90 w-full h-full relative flex justify-stretch" ref={divRef} >
        {loading && <div className="flex items-center absolute w-full h-full bg-background-gray-300 bg-opacity-20 justify-center z-[150]">
            <Spinner /> <span className="ml-2 text-xl font-semibold text-background-gray-300">Loading Map Data...</span>
        </div>}
        {error && (
            <div className="absolute inset-0 z-[150]">
                <ErrorDisplay error={error} title='Unable to Load Map Data' fullWidth />
            </div>
        )}
        {!error &&
            <>
                <BaseMap style={{width: '100%', height: '100%'}}
                    layers={layers}
                    viewState={{bounds: bounds as BBox2d, fitBoundsOptions: {padding: 20}}}
                    mapStyle={mapboxConfig.darkStyle} 
                    interactiveLayers={interactiveMapLayerIds}
                    popupOptions={popupOptions}
                    onHoverCB={onHoverCB}
                    onClickCB={onClickCB}
                    onMapRefReady={handleMapRefReady} />
                <div className=" absolute top-2 right-2 flex flex-col gap-2 z-[100]">
                    <div className='bg-background-gray-700 text-background-gray-100 p-2 flex flex-col gap-1' data-testid="layers-panel">
                        <div className='text-base font-semibold pb-1'>Layers</div>
                        {allLayers.filter(l => !l.layer.id.toString().startsWith('provider-specialty-')).map((l, i) =>
                            <div key={i} data-testid={`layer-checkbox-${l.name.toLowerCase().replace(/\s+/g, '-')}`}>
                                <Checkbox className='!text-xs' checked={!disabledLayers.includes(l.layer.id.toString())} id={i.toString()} labelText={l.name} onChange={(_, {checked}) => setLayerEnabled(l.layer.id.toString(), checked)} />
                            </div>)}
                        {allLayers.some(l => l.layer.id.toString().startsWith('provider-specialty-')) && (
                            <>
                                <button
                                    className='flex items-center justify-between text-xs text-background-gray-100 hover:text-white w-full pt-1 border-t border-background-gray-500 mt-1'
                                    onClick={() => setSpecialtiesExpanded(e => !e)}
                                >
                                    <span>Specialties ({allLayers.filter(l => l.layer.id.toString().startsWith('provider-specialty-')).length})</span>
                                    {specialtiesExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>
                                {specialtiesExpanded && allLayers.filter(l => l.layer.id.toString().startsWith('provider-specialty-')).map((l, i) =>
                                    <div key={`sp-${i}`} data-testid={`layer-checkbox-${l.name.toLowerCase().replace(/\s+/g, '-')}`}>
                                        <Checkbox className='!text-xs' checked={!disabledLayers.includes(l.layer.id.toString())} id={`sp-${i}`} labelText={l.name} onChange={(_, {checked}) => setLayerEnabled(l.layer.id.toString(), checked)} />
                                    </div>)}
                            </>
                        )}
                    </div>
                    <div className='bg-background-gray-700 text-background-gray-100 p-2'>
                        <Checkbox id='cluster' className='!text-xs justify-items-center' checked={cluster} labelText={'Enable Pin Grouping'} onChange={(_, {checked}) => setCluster(checked)} />
                    </div>
                </div>
                <div data-testid='map-legend' className="flex flex-row items-center absolute bottom-2 left-0 right-0 m-auto w-[fit-content] max-w-[calc(100%-1rem)] bg-background-gray-700 text-background-gray-100 justify-around z-[100] p-2 gap-3 flex-wrap">
                    <div className='flex flex-wrap text-xs gap-x-4 gap-y-2 items-center'>
                        {networkStore.selectedSpecialtyCount === 1 && networkStore.specialtyFilter ? (
                            <>
                                <span className='inline-flex text-xs gap-2 items-center'>
                                    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill={validColor} className='w-4' >
                                        <circle cx="50" cy="50" r="50" />
                                    </svg>
                                    <span className='text-nowrap'>In Compliance</span>
                                </span>
                                <span className='inline-flex text-xs gap-2 items-center'>
                                    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill={invalidColor} className='w-4' >
                                        <circle cx="50" cy="50" r="50" />
                                    </svg>
                                    <span className='text-nowrap'>Not In Compliance</span>
                                </span>
                            </>
                        ) : (
                            <span className='inline-flex text-xs gap-2 items-center'>
                                <svg data-testid='patients-dot' viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill={networkStore.selectedSpecialtyCount >= MULTI_SPECIALTY_THRESHOLD ? GREYED_OUT_COLOR : nullColor} className='w-4' >
                                    <circle cx="50" cy="50" r="50" />
                                </svg>
                                <span className='text-nowrap'>Patients</span>
                            </span>
                        )}

                        {networkStore.selectedSpecialtyCount >= 1 ? (
                            allLayers
                                .filter(l => (l.layer.paint as Record<string, unknown>)?.['icon-color'])
                                .map((l) => (
                                    <span key={l.name} className='inline-flex text-xs gap-2 items-center'>
                                        <LocationFilled style={{'color': (l.layer.paint as Record<string, unknown>)['icon-color'] as string}} />
                                        <span className='text-nowrap'>{l.name}</span>
                                    </span>
                                ))
                        ) : (
                            <span className='inline-flex text-xs gap-2 items-center'>
                                <LocationFilled style={{'color': providerColor}} />
                                <span className='text-nowrap'>Current Providers</span>
                            </span>
                        )}

                        {!!networkStore.localProviders.length && networkStore.activeTab === ADDITIONAL_PROVIDER_TAB_INDEX && (
                            <span className='inline-flex text-xs gap-2 items-center'>
                                <LocationFilled style={{'color': additionalProviderColor}} />
                                <span className='text-nowrap'>Additional Providers</span>
                            </span>
                        )}

                        {!!networkStore.selectedAdditionalProviders.length && networkStore.activeTab === ADDITIONAL_PROVIDER_TAB_INDEX && (
                            <span className='inline-flex text-xs gap-2 items-center'>
                                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" fill={selectedProviderColor} className='w-4' >
                                    <circle cx="50" cy="50" r="50" />
                                </svg>
                                <CheckboxCheckedFilled style={{'color': selectedProviderColor}} />
                                <span className='text-nowrap'>Selected Additional Providers</span>
                            </span>
                        )}
                    </div>
                </div>
                <div className="absolute bottom-2 right-2 z-[149] flex flex-col items-end gap-2">
                    {isFullscreen && networkStore.selectedAdditionalProviders.length && networkStore.activeTab === ADDITIONAL_PROVIDER_TAB_INDEX && <div className='bg-background-gray-700 text-background-gray-100 flex flex-col gap-1 p-2'>
                        <div className='text-base font-semibold pb-1'>Selected Providers</div>
                        {networkStore.selectedAdditionalProviders.map((p, i) => <div key={i}>{p.full_name}</div>)}
                        <SaveRosterButton
                            revisionId={revisionId}
                            setModalOpen={setSaveRosterModalOpen}
                            onUpdate={async () => {
                                try {
                                    await networkStore.updateRoster(new AbortController());
                                } catch (err) {
                                    networkStore.handleApiError(err, 'Failed to Update Roster');
                                }
                            }}
                        />
                    </div>}
                    <IconButton kind='secondary' align='top-end' onClick={requestFullScreen} label='Full Screen'><Maximize /></IconButton>
                </div>
            </>}
    </div>;
}));

NetworkMap.displayName = 'NetworkMap';

export default NetworkMap;